// src/pages/Home.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import './Home.css';
import NotificationsHorizontal from '../components/NotificationsHorizontal';

const Home = () => {
    // Data States
    const [providers, setProviders] = useState([]);
    const [paginator, setPaginator] = useState({});
    const [cities, setCities] = useState([]);
    const [recentPhysiotherapists, setRecentPhysiotherapists] = useState([]);
    const [recentClinics, setRecentClinics] = useState([]);
    const [recentRehabCentres, setRecentRehabCentres] = useState([]);
    
    // Filter States
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [role, setRole] = useState('');
    const [city, setCity] = useState('');
    const [isFiltering, setIsFiltering] = useState(false);

    useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search);
        const urlSearch = queryParams.get('search') || '';
        const urlRole = queryParams.get('role') || '';
        const urlCity = queryParams.get('city') || '';

        setSearch(urlSearch);
        setRole(urlRole);
        setCity(urlCity);

        if (urlSearch || urlRole || urlCity) {
            setIsFiltering(true);
        }

        fetchData({ search: urlSearch, role: urlRole, city: urlCity });
         // eslint-disable-next-line
    }, []);

    const fetchData = async (params = {}) => {
        setLoading(true);
        try {
            const response = await axios.get('https://physiodirectory.in/api/public-data', { params });
            const providers = response.data && response.data.providers;
            setProviders(providers && providers.data ? providers.data : []);
            setPaginator(providers || {}); // Store pagination info
            setCities((response.data && response.data.cities) || []);
            // Stats / Top lists
            setRecentPhysiotherapists((response.data && response.data.recentPhysiotherapists) || []);
            setRecentClinics((response.data && response.data.recentClinics) || []);
            setRecentRehabCentres((response.data && response.data.recentRehabCentres) || []);
        } catch (error) {
            console.error("Error fetching data:", error);
            setProviders([]);
            setPaginator({});
            setCities([]);
            setRecentPhysiotherapists([]);
            setRecentClinics([]);
            setRecentRehabCentres([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setIsFiltering(true);
        fetchData({ search, role, city });
    };

    const clearFilters = () => {
        setSearch('');
        setRole('');
        setCity('');
        setIsFiltering(false);
        fetchData({});
    };

    const handlePageChange = (newPage) => {
        fetchData({ search, role, city, page: newPage });
        // Scroll to top of results
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const ViewAllCard = ({ onClick, typeLabel }) => (
        <Link 
            to={`/users?role=${encodeURIComponent(typeLabel === 'Physiotherapist' ? 'physiotherapist' : typeLabel === 'Physio Clinic' ? 'clinic' : typeLabel === 'Rehab Centre' ? 'rehab_center' : '' )}`}
            className="card h-100 shadow-sm border-0 view-all-card d-flex flex-column align-items-center justify-content-center text-center p-3"
            style={{ cursor: 'pointer', minWidth: '160px', borderRadius: '16px', background: '#e3f2fd', textDecoration: 'none' }}
        >
            <div className="icon-circle mb-3 bg-white text-primary rounded-circle shadow-sm d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                <i className="bi bi-arrow-right" style={{ fontSize: '1.5rem' }}></i>
            </div>
            <h6 className="fw-bold text-dark mb-1">View All</h6>
            <small className="text-muted">{typeLabel}s</small>
        </Link>
    );

    const ProviderCard = ({ provider, typeLabel }) => {
        // Use pre-computed fields from API
        const { 
            profile_photo_url, 
            banner_url, 
            city: cityDisplay, 
            display_qualification: qual, 
            name: rawName,
            role
        } = provider;

        // Smart ID Resolving
        let displayId = provider.profile_id;
        if (!displayId) {
             if (role === 'physiotherapist' && provider.physiotherapist_profile) displayId = provider.physiotherapist_profile.profile_id;
             else if (role === 'clinic' && provider.clinic_profile) displayId = provider.clinic_profile.profile_id;
             else if (role === 'rehab_center' && provider.rehab_center_profile) displayId = provider.rehab_center_profile.profile_id;
        }
        // Fallback to DB ID padded if numeric, otherwise just ID
        if (!displayId && provider.id) {
            displayId = `PHY00${provider.id}`;
        } else if (displayId && !isNaN(displayId)) {
            // If displayId is numeric (e.g. from profile_id), prefix it too if needed
             displayId = `PHY00${displayId}`;
        }
        // Ensure we don't double prefix if already prefixed in DB
        if (displayId && String(displayId).startsWith('PHY') && !String(displayId).startsWith('PHY00')) {
             // If starts with PHY but not PHY00, maybe adjust? 
             // Currently assuming if it has PHY it's fine.
        } else if (displayId && !String(displayId).startsWith('PHY') && !String(displayId).startsWith('#')) {
             // If just a number (string or int), prefix
             displayId = `PHY00${displayId}`;
        }

        const displayName = role === 'physiotherapist' 
            ? `${provider.physiotherapist_profile?.prefix ? provider.physiotherapist_profile.prefix : ''} ${rawName.replace(/\sPT$/i, ' (PT)')}`
            : rawName.replace(/^Dr\.?\s*/i, '').replace(/\sPT$/i, '');
        
        const fallbackBanner = 'https://physiodirectory.in/assets/images/pd_bgm.png';
        const fallbackAvatar = typeLabel === 'Physiotherapist' 
            ? 'https://physiodirectory.in/assets/images/pd_image.png' 
            : (typeLabel === 'Physio Clinic' ? 'https://physiodirectory.in/assets/images/clinic.png' : 'https://physiodirectory.in/assets/images/rahab.png');

        return (
            <div className="card provider-card-modern h-100 shadow-sm border-0 bg-white">
                <div className="card-banner overflow-hidden position-relative" style={{height: '140px'}}>
                     <img 
                        src={banner_url || fallbackBanner} 
                        alt="Banner" 
                        className="w-100 h-100 object-fit-cover"
                        onError={(e) => {e.target.onerror = null; e.target.src = fallbackBanner;}}
                    />
                </div>
                
                <div className="card-body p-3 d-flex flex-column bg-white">
                    <div className="d-flex align-items-start mb-3">
                         {/* Avatar Wrapper */}
                        <div className="position-relative me-3 flex-shrink-0" style={{width: '60px', height: '60px'}}>
                            <img 
                                src={profile_photo_url || fallbackAvatar} 
                                alt={displayName} 
                                className="rounded-circle bg-white shadow-sm"
                                style={{
                                    width: '100%', 
                                    height: '100%', 
                                    objectFit: 'cover', 
                                    padding: '3px' // slight padding for aesthetic
                                }}
                                onError={(e) => {e.target.onerror = null; e.target.src = fallbackAvatar;}}
                            />
                        </div>
                        
                        {/* Name & Badge */}
                        <div className="flex-grow-1 pt-1 overflow-hidden">
                            <h5 className="fw-bold mb-1 text-dark text-truncate" style={{fontSize: '1.05rem', color: '#2d3748'}}>
                                {displayName}
                            </h5>
                            <div className="d-flex flex-column align-items-start gap-1">
                                <span className="badge bg-primary-subtle text-primary fw-medium rounded-2 px-2 py-1" style={{fontSize: '0.7rem'}}>
                                    {typeLabel}
                                </span>
                                <small className="text-muted" style={{fontSize: '0.75rem'}}>ID: {displayId}</small>
                            </div>
                        </div>
                    </div>
                    
                    {/* Details */}
                    <div className="mb-3">
                        <p className="text-muted small mb-1 d-flex align-items-center">
                            <i className="bi bi-geo-alt-fill me-2 text-primary"></i>
                            <span className="text-truncate">{cityDisplay && cityDisplay !== 'N/A' ? cityDisplay : 'Location N/A'}</span>
                        </p>
                        {qual && (
                            <p className="text-muted small mb-0 d-flex align-items-center">
                                <i className="bi bi-award me-2 text-primary"></i>
                                <span className="text-truncate">{qual}</span>
                            </p>
                        )}
                    </div>
                    
                    {/* View Profile Button */}
                    <Link to={`/provider/${provider.id}`} className="btn btn-outline-primary w-100 rounded-pill fw-semibold mt-auto btn-sm" style={{fontSize: '0.9rem'}}>
                        View Profile <i className="bi bi-arrow-right ms-1"></i>
                    </Link>
                </div>
            </div>
        );
    };

    return (
        <div className="home-page pb-5 w-100 mobile-app-layout">
            
            {/* Mobile App Header & Search - Redesigned */}
            <div className="home-header-section sticky-top shadow-sm">
                
                {/* Top Location Bar */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="d-flex align-items-center">
                        <div>
                            <small className="text-white-50 d-block" style={{fontSize: '0.75rem'}}>Location</small>
                            <div className="d-flex align-items-center text-white">
                                <i className="bi bi-geo-alt-fill me-1 small"></i>
                                <select 
                                    className="form-select border-0 bg-transparent text-white p-0 fw-bold custom-city-select"
                                    style={{width: 'auto', boxShadow: 'none', cursor: 'pointer', appearance: 'none', paddingRight: '0', backgroundImage: 'none'}}
                                    value={city}
                                    onChange={(e) => {
                                        setCity(e.target.value); 
                                        if(e.target.value) { setIsFiltering(true); fetchData({city: e.target.value, role, search}); }
                                    }}
                                >
                                    <option value="" className="text-dark">All Cities</option>
                                    {cities.map((c, index) => (
                                        <option key={index} value={c} className="text-dark">{c}</option>
                                    ))}
                                </select>
                                <i className="bi bi-chevron-down ms-1" style={{fontSize: '0.7rem'}}></i>
                            </div>
                        </div>
                    </div>
                    <button
                        className="btn fw-bold rounded-pill px-4 py-2 ms-2"
                        style={{fontSize: '1rem', background: '#0a1435', color: '#fff'}}
                        onClick={() => {
                            const url = 'https://physiodirectory.in/register';
                            if (window.ReactNativeWebView) {
                                window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'openExternal', url }));
                            } else {
                                window.open(url, '_blank');
                            }
                        }}
                    >
                        Join Now
                    </button>
                </div>

                <form onSubmit={handleSearch}>
                    <div className="search-input-wrapper">
                        <i className="bi bi-search"></i>
                        <input 
                            type="text" 
                            className="form-control modern-search-input" 
                            placeholder="Search Doctors, Clinics..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                </form>
                
                {/* Horizontal Category Filters */}
                <div className="category-scroll-container mt-3 mb-0 ps-0">
                    <div 
                        className={`category-pill ${role === '' ? 'active' : ''}`}
                        onClick={() => { setRole(''); setIsFiltering(false); fetchData({}); }}
                    >
                        All
                    </div>
                    <div 
                        className={`category-pill ${role === 'physiotherapist' ? 'active' : ''}`}
                        onClick={() => { setRole('physiotherapist'); setIsFiltering(true); fetchData({ role: 'physiotherapist' }); }}
                    >
                        Physiotherapist
                    </div>
                    <div 
                        className={`category-pill ${role === 'clinic' ? 'active' : ''}`}
                        onClick={() => { setRole('clinic'); setIsFiltering(true); fetchData({ role: 'clinic' }); }}
                    >
                        Clinic
                    </div>
                        <div 
                            className={`category-pill ${role === 'rehab_center' ? 'active' : ''}`}
                            onClick={() => { setRole('rehab_center'); setIsFiltering(true); fetchData({ role: 'rehab_center' }); }}
                        >
                            Rehab Centre
                        </div>
                </div>
            </div>

            {loading ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            ) : (
                <>
                {/* Default Home View */}
                {!isFiltering && (
                    <div className="container-fluid px-3 pb-5 pt-3">
                        {/* Verified Physiotherapists Section */}
                        {recentPhysiotherapists.length > 0 && (
                            <div className="mb-2 section-block">
                                <div className="section-title-row d-flex flex-column align-items-start">
                                    <div className="text-muted mb-0" style={{fontSize: '1rem', lineHeight: 1.2}}>Meet Our Professionals</div>
                                    <h2 className="fw-bold mt-1 mb-0 text-dark" style={{fontSize: '1.1rem'}}>Verified Physiotherapists</h2>
                                </div>
                                <div className="horizontal-scroll-section">
                                    {recentPhysiotherapists.map(p => (
                                        <div className="horizontal-card" key={p.id}>
                                            <ProviderCard provider={p} typeLabel="Physiotherapist" />
                                        </div>
                                    ))}
                                    {/* View All Card */}
                                    <div className="horizontal-card" style={{ minWidth: '160px', maxWidth: '160px' }}>
                                         <ViewAllCard typeLabel="Physiotherapist" />
                                    </div>
                                </div>
                                <div className="app-section-spacer"></div>
                            </div>
                        )}

                        {/* Verified Physio Clinics Section */}
                        {recentClinics.length > 0 && (
                            <div className="mb-2 section-block">
                                <div className="section-title-row pt-3 d-flex flex-column align-items-start">
                                    <div className="text-muted mb-0" style={{fontSize: '1rem', lineHeight: 1.2}}>Meet Our Experts</div>
                                    <h2 className="fw-bold mt-1 mb-0 text-dark" style={{fontSize: '1.1rem'}}>Verified Physio Clinics</h2>
                                </div>
                                <div className="horizontal-scroll-section">
                                    {recentClinics.map(p => (
                                        <div className="horizontal-card" key={p.id}>
                                            <ProviderCard provider={p} typeLabel="Physio Clinic" />
                                        </div>
                                    ))}
                                    {/* View All Card */}
                                    <div className="horizontal-card" style={{ minWidth: '160px', maxWidth: '160px' }}>
                                         <ViewAllCard typeLabel="Physio Clinic" />
                                    </div>
                                </div>
                                <div className="app-section-spacer"></div>
                            </div>
                        )}

                        {/* Verified Rehab Centres Section - NOW HORIZONTAL */}
                        {recentRehabCentres.length > 0 && (
                            <div className="mb-2 section-block">
                                <div className="section-title-row pt-3 d-flex flex-column align-items-start">
                                    <div className="text-muted mb-0" style={{fontSize: '1rem', lineHeight: 1.2}}>Meet Our Experts</div>
                                    <h2 className="fw-bold mt-1 mb-0 text-dark" style={{fontSize: '1.1rem'}}>Verified Rehab Centres</h2>
                                </div>
                                <div className="horizontal-scroll-section">
                                    {recentRehabCentres.map(p => (
                                        <div className="horizontal-card" key={p.id}>
                                            <ProviderCard provider={p} typeLabel="Rehab Centre" />
                                        </div>
                                    ))}
                                     {/* View All Card */}
                                     <div className="horizontal-card" style={{ minWidth: '160px', maxWidth: '160px' }}>
                                         <ViewAllCard typeLabel="Rehab Centre" />
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Notifications Horizontal Section */}
                        <NotificationsHorizontal />
                    </div>
                )}


                {/* Filtered Results View */}
                {isFiltering && (
                    <div className="container px-3 pt-3">
                         <div className="d-flex justify-content-between align-items-center mb-4">
                            <h4 className="fw-bold mb-0">Search Results</h4>
                            <span className="text-muted small">{paginator.total || 0} found</span>
                        </div>
                        
                        <div className="row g-3">
                            {providers.length > 0 ? (
                                providers.map(p => (
                                    <div className="col-12 col-md-6 col-lg-4" key={p.id}>
                                        <ProviderCard 
                                            provider={p} 
                                            typeLabel={p.role === 'physiotherapist' ? 'Physiotherapist' : (p.role === 'clinic' ? 'Physio Clinic' : 'Rehab Centre')} 
                                        />
                                    </div>
                                ))
                            ) : (
                                <div className="text-center py-5">
                                    <i className="bi bi-search display-1 text-light"></i>
                                    <p className="text-muted mt-3">No providers found matching your criteria.</p>
                                    <button onClick={clearFilters} className="btn btn-outline-primary btn-sm rounded-pill mt-2">Clear Filters</button>
                                </div>
                            )}
                        </div>
                        
                        {/* Pagination */}
                        {providers.length > 0 && paginator && paginator.last_page > 1 && (
                            <div className="d-flex justify-content-center align-items-center mb-5 pb-5 gap-3 mt-4">
                                <button 
                                    className={`btn rounded-circle shadow p-0 d-flex align-items-center justify-content-center ${paginator.current_page === 1 ? 'btn-light text-muted opacity-50' : 'btn-primary text-white'}`}
                                    style={{width: '48px', height: '48px', transition: 'all 0.2s'}}
                                    disabled={paginator.current_page === 1}
                                    onClick={() => handlePageChange(paginator.current_page - 1)}
                                >
                                    <i className="bi bi-arrow-left fs-5"></i>
                                </button>
                                
                                <span className="fw-bold text-dark mx-2 bg-white px-4 py-2 rounded-pill shadow-sm">
                                    <span className="text-primary">{paginator.current_page}</span> 
                                    <span className="text-black-50 small mx-1">of</span> 
                                    {paginator.last_page}
                                </span>
                                
                                <button 
                                    className={`btn rounded-circle shadow p-0 d-flex align-items-center justify-content-center ${paginator.current_page === paginator.last_page ? 'btn-light text-muted opacity-50' : 'btn-primary text-white'}`}
                                    style={{width: '48px', height: '48px', transition: 'all 0.2s'}}
                                    disabled={paginator.current_page === paginator.last_page}
                                    onClick={() => handlePageChange(paginator.current_page + 1)}
                                >
                                    <i className="bi bi-arrow-right fs-5"></i>
                                </button>
                            </div>
                        )}
                    </div>
                )}
                </>
            )}
        </div>
    );
};

export default Home;
